import { computed, ref, type Ref } from "vue";

const STORAGE_KEY = "ob-theme";

export type ThemeMode = "dark" | "light";

function readStored(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  return "dark";
}

function applyDom(mode: ThemeMode) {
  document.documentElement.setAttribute("data-theme", mode);
  document.documentElement.style.colorScheme = mode;
}

const mode = ref<ThemeMode>(readStored());
applyDom(mode.value);

export function useTheme() {
  const isDark = computed(() => mode.value === "dark");
  const isLight = computed(() => mode.value === "light");

  function setTheme(next: ThemeMode) {
    mode.value = next;
    applyDom(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }

  function toggleTheme() {
    setTheme(mode.value === "dark" ? "light" : "dark");
  }

  return {
    mode: mode as Ref<ThemeMode>,
    isDark,
    isLight,
    setTheme,
    toggleTheme,
  };
}

/** Call once at app boot (before mount is fine). */
export function initTheme() {
  applyDom(mode.value);
}
